<template>
  <div class="event-layout preparation-page">
    <EventSidebar :event-id="eventId" />

    <main class="event-main">
      <div class="mb-6">
        <p class="text-sm font-semibold text-gray-500">Events → {{ eventName }}</p>
        <h1 class="text-3xl tracking-tight font-black text-primary mt-2">Setup & import</h1>
      </div>

      <div v-if="error" class="notice error">{{ error }}</div>
      <div v-if="successMessage" class="notice success">{{ successMessage }}</div>

      <p v-if="loadingEvent" role="status">Loading event setup…</p>
      <button v-if="!loadingEvent && !eventLoaded" class="secondary-button" @click="loadEvent">Retry loading setup</button>
      <template v-if="eventLoaded && !loadingEvent">
      <section class="panel mb-6">
        <h2 class="panel-title">Event Details</h2>
        <div class="grid gap-4 md:grid-cols-3">
          <div>
            <label class="field-label">Event Name</label>
            <input v-model="eventForm.name" type="text" class="field-input" />
          </div>
          <div>
            <label class="field-label">University Slug</label>
            <input v-model="eventForm.universitySlug" type="text" class="field-input" />
          </div>
          <div>
            <label class="field-label">Status</label>
            <select v-model="eventForm.status" class="field-input">
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option v-if="canArchiveEvents" value="archived">Archived</option>
            </select>
          </div>
        </div>
        <div class="mt-6">
          <button class="primary-button" @click="saveEventSetup" :disabled="savingEvent">
            {{ savingEvent ? "Saving..." : "Save Event Details" }}
          </button>
        </div>
      </section>

      <section class="panel mb-6" aria-labelledby="workflow-title">
        <h2 id="workflow-title" class="panel-title">How will you use Orator Pro?</h2>
        <p class="text-secondary mt-2">These choices shape your preparation checklist. {{ planSaved ? 'Workflow saved.' : 'Choose a workflow to get a checklist for your ceremony.' }}</p>
        <p v-if="participantsLocked" class="notice mt-4">The workflow is fixed after publication.</p>
        <fieldset :disabled="participantsLocked || savingPlan" class="mt-5">
          <div class="grid gap-4 md:grid-cols-2">
            <label class="field-label">What are you preparing?
              <select v-model="planForm.purpose" class="field-input mt-2">
                <option value="live">Full live ceremony</option><option value="pronunciation">Pronunciation preparation only</option>
              </select>
              <small class="block mt-2 font-normal">{{ planForm.purpose === 'live' ? 'Prepare names, manage attendance and run the ceremony screens.' : 'Review names, rehearse and download your materials. No live queue needed.' }}</small>
            </label>
            <label class="field-label">Pronunciation source
              <select v-model="planForm.pronunciation" class="field-input mt-2">
                <option value="generated">Generate pronunciation audio</option><option value="supplied">Use supplied pronunciation information</option>
              </select>
              <small class="block mt-2 font-normal">{{ planForm.pronunciation === 'generated' ? 'Names without ready audio appear in your checklist.' : 'Use your own phonetics, recordings and notes. Generated audio is optional.' }}</small>
            </label>
            <label v-if="planForm.purpose === 'live'" class="field-label">Attendance approach
              <select v-model="planForm.attendance" class="field-input mt-2">
                <option value="check_in">Check graduates in on arrival</option><option value="expected">Start with an expected list; remove absences</option>
              </select>
              <small class="block mt-2 font-normal">{{ planForm.attendance === 'check_in' ? 'Checking in includes the graduate in the queue at their planned position.' : 'Include the expected graduates in Graduate list, then exclude absences at the desk.' }} Saving this choice does not change existing inclusion.</small>
            </label>
          </div>
          <h3 class="font-bold mt-6">Who is responsible?</h3>
          <p class="text-secondary text-sm mt-1">Name a person or team for each job. One person can cover several jobs. Account access is managed separately.</p>
          <div class="grid gap-4 md:grid-cols-2 mt-4">
            <label v-for="job in planJobs" :key="job.key" class="field-label">{{ job.label }}
              <input v-model="planForm.responsibilities[job.key]" type="text" maxlength="120" class="field-input mt-2" placeholder="Person or team" />
            </label>
          </div>
          <button class="primary-button mt-6" @click="saveCeremonyPlan">{{ savingPlan ? 'Saving…' : 'Save workflow & responsibilities' }}</button>
        </fieldset>
      </section>

      <section class="panel mb-6">
        <h2 class="panel-title">Student Pronunciation Collection</h2>
        <p class="text-secondary font-medium mt-2">
          Configure whether students can use review links for this event.
        </p>
        <div class="settings-grid mt-5">
          <label class="setting-row">
            <input
              v-model="eventForm.studentPronunciationCollection.enabled"
              type="checkbox"
              :disabled="savingCollectionSettings"
              @change="saveStudentCollectionSettings"
            />
            <span>
              <strong>Enable student review links</strong>
              <small>Students with a secure link can review generated audio and submit a correction until the queue is published.</small>
            </span>
          </label>
          <label
            class="setting-row"
            :class="{ disabled: !eventForm.studentPronunciationCollection.enabled }"
          >
            <input
              v-model="eventForm.studentPronunciationCollection.emailEnabled"
              type="checkbox"
              :disabled="!eventForm.studentPronunciationCollection.enabled || savingCollectionSettings"
              @change="saveStudentCollectionSettings"
            />
            <span>
              <strong>Allow students to request email links</strong>
              <small>Students can enter their email on the public review page and receive their secure link by email.</small>
            </span>
          </label>
        </div>
        <p v-if="collectionSettingsMessage" class="setting-status">
          {{ collectionSettingsMessage }}
        </p>
      </section>

      <section class="panel">
        <div class="flex flex-wrap justify-between gap-4 items-start">
          <div>
            <h2 class="panel-title">CSV Import</h2>
            <p class="text-secondary font-medium mt-2">
              Import participants for this event. Imported rows go to the Participants page, not directly to the live queue.
            </p>
          </div>
          <router-link :to="participantsPath" class="secondary-link">
            Go to Participants
          </router-link>
        </div>

        <p class="import-steps">1. Choose a CSV → 2. Match columns → 3. Preview and import</p>
        <p v-if="participantsLocked" class="notice">This roster is locked after publishing. Add late arrivals from Run ceremony.</p>
        <div v-else-if="existingCount" class="import-warning">
          <strong>This import replaces the existing {{ existingCount }} roster entries.</strong>
          <p>It is not an update or merge. Existing preparation and queue inclusion can be lost. Download a copy of the roster before continuing.</p>
          <label><input v-model="replaceConfirmed" type="checkbox" /> I intend to replace this ceremony roster.</label>
        </div>
        <div class="mt-6 grid gap-4 md:grid-cols-2">
          <div>
            <label class="field-label">CSV File</label>
            <input type="file" accept=".csv,text/csv" @change="onFileChange" />
          </div>
          <div>
            <label class="field-label">Default Type</label>
            <select v-model="defaultType" class="field-input">
              <option value="student">student</option>
              <option value="presenter_note">presenter_note</option>
              <option value="display_line">display_line</option>
            </select>
          </div>
        </div>

        <div class="mt-6 flex flex-wrap gap-3">
          <button class="secondary-button" @click="analyzeImport" :disabled="!csvText || importLoading || participantsLocked">
            {{ importLoading ? "Reading..." : "Read Headers" }}
          </button>
          <button class="primary-button" @click="previewMappedImport" :disabled="!csvText || importLoading || participantsLocked">
            Preview Import
          </button>
          <button
            class="primary-button"
            @click="commitPreview"
            :disabled="previewItems.length === 0 || previewErrors.length > 0 || importLoading || participantsLocked || (existingCount > 0 && !replaceConfirmed)"
          >
            Import Participants
          </button>
        </div>

        <div v-if="headers.length" class="mt-6 grid gap-4 md:grid-cols-5">
          <div>
            <label class="field-label">Type Column</label>
            <select v-model="mapping.type" class="field-input">
              <option value="">None</option>
              <option v-for="header in headers" :key="`type-${header}`" :value="header">{{ header }}</option>
            </select>
          </div>
          <div>
            <label class="field-label">Full Name Column</label>
            <select v-model="mapping.full_name" class="field-input">
              <option value="">None</option>
              <option v-for="header in headers" :key="`name-${header}`" :value="header">{{ header }}</option>
            </select>
          </div>
          <div>
            <label class="field-label">Award Column</label>
            <select v-model="mapping.award" class="field-input">
              <option value="">None</option>
              <option v-for="header in headers" :key="`award-${header}`" :value="header">{{ header }}</option>
            </select>
          </div>
          <div>
            <label class="field-label">Email Column</label>
            <select v-model="mapping.email" class="field-input">
              <option value="">None</option>
              <option v-for="header in headers" :key="`email-${header}`" :value="header">{{ header }}</option>
            </select>
          </div>
          <div>
            <label class="field-label">Text Column</label>
            <select v-model="mapping.text" class="field-input">
              <option value="">None</option>
              <option v-for="header in headers" :key="`text-${header}`" :value="header">{{ header }}</option>
            </select>
          </div>
        </div>

        <div v-if="previewErrors.length" class="mt-6">
          <h3 class="text-lg font-bold text-primary">Import Errors</h3>
          <ul class="list-disc ml-6 mt-2 text-red-700">
            <li v-for="errorText in previewErrors" :key="errorText">{{ errorText }}</li>
          </ul>
        </div>

        <div v-if="previewItems.length" class="mt-6">
          <h3 class="text-lg font-bold text-primary">Preview</h3>
          <div class="mt-3 space-y-2">
            <div
              v-for="item in previewItems"
              :key="item.id"
              class="preview-row"
            >
              <strong class="capitalize">{{ item.type.replace("_", " ") }}</strong>
              <span v-if="item.type === 'student'">: {{ item.fullName }} / {{ item.award }}</span>
              <span v-else>: {{ item.text }}</span>
            </div>
          </div>
        </div>
      </section>
      </template>
    </main>
  </div>
</template>

<script>
import { commitImport, fetchEvent, previewImport, saveEvent } from "@/lib/api";
import { hasAnyRole } from "@/lib/auth";
import EventSidebar from "./EventSidebar.vue";

export default {
  name: "EventSetupView",
  components: {
    EventSidebar,
  },
  data() {
    return {
      loadingEvent: true,
      eventLoaded: false,
      eventForm: {
        name: "",
        status: "draft",
        universitySlug: "",
        studentPronunciationCollection: {
          enabled: false,
          emailEnabled: false,
        },
      },
      planForm: { purpose: 'live', attendance: 'check_in', pronunciation: 'generated', responsibilities: {} },
      planSaved: false,
      savingPlan: false,
      loadedQueueRevision: null,
      existingCount: 0,
      participantsLocked: false,
      replaceConfirmed: false,
      csvText: "",
      headers: [],
      mapping: {
        type: "",
        full_name: "",
        award: "",
        email: "",
        text: "",
      },
      defaultType: "student",
      previewItems: [],
      previewErrors: [],
      savingEvent: false,
      savingCollectionSettings: false,
      importLoading: false,
      collectionSettingsMessage: "",
      error: "",
      successMessage: "",
    };
  },
  computed: {
    planJobs() {
      const jobs = [{ key: 'organiser', label: 'Ceremony lead · roster & decisions' }, { key: 'orator', label: 'Orator · rehearsal & name checks' }];
      if (this.planForm.purpose === 'live') jobs.push({ key: 'checkIn', label: 'Check-in team · arrivals & corrections' }, { key: 'queue', label: 'Queue operator · running order & late arrivals' }, { key: 'av', label: 'AV team · presenter & venue screens' });
      return jobs;
    },
    eventId() {
      return this.$route.params.eventId;
    },
    eventName() {
      return this.eventForm.name || this.eventId;
    },
    participantsPath() {
      return `/events/${encodeURIComponent(this.eventId)}/participants`;
    },
    canArchiveEvents() {
      return hasAnyRole(["internal_admin"]);
    },
  },
  watch: {
    mapping: { deep: true, handler() { this.previewItems = []; this.previewErrors = []; } },
    defaultType() { this.previewItems = []; this.previewErrors = []; },
  },
  methods: {
    async loadEvent() {
      this.loadingEvent = true; this.eventLoaded = false;
      this.error = "";
      try {
        const snapshot = await fetchEvent(this.eventId);
        this.eventLoaded = true;
        this.planSaved = Boolean(snapshot.event?.ceremonyPlan);
        this.planForm = JSON.parse(JSON.stringify(snapshot.event?.ceremonyPlan || { purpose: 'live', attendance: 'check_in', pronunciation: 'generated', responsibilities: {} }));
        this.loadedQueueRevision = snapshot.queue.revision;
        this.existingCount = snapshot.participants?.items?.length || 0;
        this.participantsLocked = Boolean(snapshot.participants?.locked);
        this.eventForm = {
          name: snapshot.event?.name || this.eventId,
          status: snapshot.event?.status || "draft",
          universitySlug: snapshot.event?.universitySlug || "",
          studentPronunciationCollection: this.normalizeStudentPronunciationCollection(
            snapshot.event?.studentPronunciationCollection
          ),
        };
      } catch (error) {
        this.error = error?.response?.data?.error || 'Unable to load event setup. Retry before making changes.';
      } finally { this.loadingEvent = false; }
    },
    normalizeStudentPronunciationCollection(settings = {}) {
      const enabled = Boolean(settings?.enabled);
      return {
        enabled,
        emailEnabled: enabled && Boolean(settings?.emailEnabled),
      };
    },
    async saveCeremonyPlan() {
      this.savingPlan = true; this.error = ''; this.successMessage = '';
      try {
        await saveEvent(this.eventId, { ...this.eventForm, ceremonyPlan: this.planForm });
        this.planSaved = true;
        this.successMessage = 'Workflow and responsibilities saved. Your Overview now reflects these choices.';
      } catch (error) {
        this.error = error?.response?.data?.error || 'Unable to save the workflow.';
      } finally { this.savingPlan = false; }
    },
    async saveEventSetup() {
      this.savingEvent = true;
      this.error = "";
      this.successMessage = "";
      try {
        const payload = {
          ...this.eventForm,
          studentPronunciationCollection: this.normalizeStudentPronunciationCollection(
            this.eventForm.studentPronunciationCollection
          ),
        };
        const saved = await saveEvent(this.eventId, payload);
        if (this.loadedQueueRevision === null) this.loadedQueueRevision = saved.queue.revision;
        this.successMessage = "Event details saved.";
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to save event details.";
      } finally {
        this.savingEvent = false;
      }
    },
    async saveStudentCollectionSettings() {
      const settings = this.normalizeStudentPronunciationCollection(
        this.eventForm.studentPronunciationCollection
      );
      this.eventForm.studentPronunciationCollection = settings;
      this.savingCollectionSettings = true;
      this.collectionSettingsMessage = "Saving...";
      this.error = "";
      this.successMessage = "";
      try {
        const snapshot = await saveEvent(this.eventId, {
          ...this.eventForm,
          studentPronunciationCollection: settings,
        });
        this.eventForm.studentPronunciationCollection = this.normalizeStudentPronunciationCollection(
          snapshot.event?.studentPronunciationCollection
        );
        this.collectionSettingsMessage = "Saved.";
      } catch (error) {
        this.collectionSettingsMessage = "";
        this.error = error?.response?.data?.error || "Unable to save collection settings.";
        await this.loadEvent();
      } finally {
        this.savingCollectionSettings = false;
      }
    },
    async onFileChange(event) {
      const [file] = event.target.files || [];
      this.previewItems = [];
      this.previewErrors = [];
      this.headers = [];
      this.replaceConfirmed = false;
      this.mapping = { type: "", full_name: "", award: "", email: "", text: "" };
      if (!file) {
        this.csvText = "";
        return;
      }
      this.csvText = await file.text();
    },
    async analyzeImport() {
      this.importLoading = true;
      this.error = "";
      try {
        const result = await previewImport(this.eventId, {
          csvText: this.csvText,
          mapping: {},
          defaultType: this.defaultType,
        });
        this.headers = result.headers || [];
        // A header read has no mapping yet, so row-validation errors are premature.
        this.previewErrors = this.headers.length ? [] : result.errors || [];
        const aliases = { full_name: ['fullname', 'studentname', 'name'], award: ['award', 'degree', 'qualification'], email: ['email', 'emailaddress'], type: ['type', 'kind'], text: ['text'] };
        for (const [key, names] of Object.entries(aliases)) {
          this.mapping[key] = this.headers.find(header => names.includes(header.toLowerCase().replace(/[^a-z]/g, ''))) || '';
        }
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to read CSV headers.";
      } finally {
        this.importLoading = false;
      }
    },
    async previewMappedImport() {
      this.importLoading = true;
      this.error = "";
      try {
        const result = await previewImport(this.eventId, {
          csvText: this.csvText,
          mapping: this.mapping,
          defaultType: this.defaultType,
        });
        this.headers = result.headers || [];
        this.previewItems = result.items || [];
        this.previewErrors = result.errors || [];
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to preview import.";
      } finally {
        this.importLoading = false;
      }
    },
    async commitPreview() {
      if (this.participantsLocked || (this.existingCount > 0 && !this.replaceConfirmed)) return;
      this.importLoading = true;
      this.error = "";
      this.successMessage = "";
      try {
        const saved = await commitImport(this.eventId, this.previewItems, this.loadedQueueRevision);
        this.loadedQueueRevision = saved.queue.revision;
        this.existingCount = saved.participants.items.length;
        this.replaceConfirmed = false;
        this.successMessage = "Participants imported. Continue in Participants to review and prepare the roster.";
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to import participants.";
      } finally {
        this.importLoading = false;
      }
    },
  },
  mounted() {
    this.loadEvent();
  },
};
</script>

<style scoped>
.import-steps { margin-top: 1rem; color: var(--orator-graphite); font-size: 0.9rem; }
.import-warning { padding: 1rem; margin-top: 1rem; background: #fffbeb; border: 1px solid #fde68a; border-radius: 0.5rem; color: #854d0e; font-size: 0.9rem; }
.import-warning p { margin: 0.5rem 0; }
.import-warning label { display: flex; gap: 0.65rem; align-items: center; }

.event-layout {
  display: flex;
  gap: 1rem;
  width: calc(100vw - 0.25rem);
  max-width: none;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 0 0.125rem 2.5rem;
}

.event-main {
  flex: 1 1 auto;
  min-width: 0;
}

:deep(.sidebar) {
  width: 168px;
  flex-basis: 168px;
}

.panel {
  background: white;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.06);
  padding: 1.5rem;
}

.panel-title {
  font-size: 1.5rem;
  font-weight: 900;
  color: var(--orator-navy);
}

.field-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--orator-arsenic);
  margin-bottom: 0.5rem;
}

.field-input {
  display: block;
  width: 100%;
  border-radius: 0.375rem;
  border: 1px solid var(--orator-steel);
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  padding: 0.625rem 0.75rem;
}

.settings-grid {
  display: grid;
  gap: 0.8rem;
}

.setting-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.5rem;
  padding: 0.95rem;
}

.setting-row input {
  margin-top: 0.2rem;
}

.setting-row strong,
.setting-row small {
  display: block;
}

.setting-row strong {
  color: var(--orator-navy);
  font-weight: 850;
}

.setting-row small {
  margin-top: 0.25rem;
  color: var(--orator-graphite);
  line-height: 1.45;
}

.setting-row.disabled {
  opacity: 0.62;
}

.setting-status {
  margin-top: 0.8rem;
  color: var(--orator-graphite);
  font-size: 0.86rem;
  font-weight: 700;
}

.primary-button,
.secondary-button,
.secondary-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.625rem 0.95rem;
  border-radius: 0.375rem;
  font-weight: 700;
  text-decoration: none;
  border: 1px solid var(--orator-steel);
}

.primary-button {
  background: var(--orator-blue);
  border-color: var(--orator-blue);
  color: white;
}

.secondary-button,
.secondary-link {
  background: white;
  color: var(--orator-arsenic);
}

.preview-row {
  background: white;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
}

.notice {
  margin-bottom: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 0.65rem;
  font-weight: 600;
}

.notice.error {
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.notice.success {
  color: #166534;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

@media (max-width: 960px) {
  .event-layout {
    width: auto;
    flex-direction: column;
    padding: 0 1rem 2.5rem;
  }

  :deep(.sidebar) {
    width: 100%;
    flex-basis: 100%;
  }
}
</style>
